import { commonFiltersByName } from "@hightouch/lib/templating/functions";
import { Liquid } from "liquidjs";

/**
 * Disabled filesystem implementation that throws on every operation. Mirrors
 * the protection in `@hightouch/lib/templating/liquid` so the client-side
 * engine has the same security baseline as the server engine — even though
 * the browser bundle has no real `fs`, the explicit throws document intent
 * and guard against `{% include %}` / `{% render %}` resolution attempts.
 *
 * See https://www.hacefresko.com/posts/liquidjs-ssti-to-arbitrary-file-read.
 *
 * NOTE: The lib wrapper isn't reused directly so the app bundle keeps its own
 * engine construction; the app's `liquidjs` is pinned to the same version the
 * wrapper resolves, so the two never diverge in behavior.
 */
const throwOnFsAccess = (op: string) => (path: string) => {
  throw new Error(`Unauthorized Liquid template ${op} for path: ${path}`);
};

export const disabledLiquidFs = {
  readFileSync: throwOnFsAccess("readFileSync"),
  readFile: async (p: string) => throwOnFsAccess("readFile")(p),
  existsSync: throwOnFsAccess("existsSync"),
  exists: async (p: string) => throwOnFsAccess("exists")(p),
  resolve: (dir: string, file: string, ext: string) => {
    throw new Error(
      `Unauthorized Liquid template resolve for ${dir}/${file}${ext}`,
    );
  },
};

/**
 * Client-side Liquid engine used by the template preview modal and the v10
 * sync-config validator. Mirrors the server-side `getEngine()` factory in
 * `packages/backend/sdk/lib/liquid-template/liquid.ts` as closely as
 * possible so the preview matches what production will render:
 *
 * - Filesystem access disabled (matches lib wrapper).
 * - `keepOutputType: true` and `cache: true` match the server config.
 *
 * - `commonFunctions` filters (sha256, cast, b64_encode, …) are registered
 *   below from the browser-safe `commonFiltersByName` map, so previews render
 *   them accurately instead of silently dropping them. They are registered by
 *   EXPLICIT name because this is a minified bundle where `Function.name` is
 *   mangled — a `fn.name`-based loop would bind every filter to a garbage name.
 *   `strictFilters` stays `false` so a destination's OWN custom filters (which
 *   live in backend code and aren't available in the browser) gracefully no-op
 *   rather than erroring.
 *
 * Known parity gaps (tracked as follow-ups):
 * - Sync-context variables from `getLiquidContext` (model_id, sync_id,
 *   timestamp, etc.) are not yet wired into the preview scope — only
 *   `{ row }` is available.
 */
export const v10Engine = new Liquid({
  fs: disabledLiquidFs,
  keepOutputType: true,
  strictFilters: false,
  cache: true,
  ownPropertyOnly: false,
  relativeReference: false,
});

// Register the common Hightouch filters so previews render them accurately.
// Without this, `strictFilters: false` silently no-ops them — e.g. `| sha256`
// would echo the raw input, producing wrong-but-error-free preview output.
// Registered by EXPLICIT name (object keys survive minification; `Function.name`
// does not — see `commonFiltersByName`).
for (const [name, fn] of Object.entries(commonFiltersByName)) {
  v10Engine.registerFilter(name, fn as Parameters<Liquid["registerFilter"]>[1]);
}
